import { IconPlus } from '@tabler/icons-react';
import { ColDef, IRowNode } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import DataTable from 'components/tables/DataTable';
import { hrActionCellVersion, HrConfigurationActions } from 'components/tables/data/hr/HrActions';
import {
  HrConfigurationNameCell,
  HrIdCell,
  HrLongTextCell,
  HrScopeCell,
  HrTextCell,
} from 'components/tables/data/hr/HrTableCells';
import panic from 'errors/panic';
import {
  getResponsiveActionsColumnWidth,
  SINGLE_ACTIONS_COLUMN_WIDTH,
  useResponsiveDesign,
} from 'hooks/use-responsive-design';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, HR_CREATE_FILE_CATEGORY_PAGE_PATH, HR_EDIT_FILE_CATEGORY_PAGE_PATH } from 'routes/paths';

/** Administrator list of global and multi-organization HR file categories. */
export default function HrFileCategoriesTable() {
  const { getAction, roleId } = useApi();
  const { tableActionMode } = useResponsiveDesign();
  const [refreshToken, setRefreshToken] = useState(0);

  const load = useCallback(async () => {
    const response = await getAction('HrListFileCategories')({ query: { filters: { includeArchived: true } } });

    return response.map((category) => ({
      ...category,
      rawStatus: category.status,
      _lifecycleActive: category.status === 'active' ? '1' : '0',
    }));
  }, [getAction]);

  const toggleStatus = useCallback(
    async (category: any, node: IRowNode<any>) => {
      try {
        const nextStatus = category.rawStatus === 'active' ? 'archived' : 'active';
        const updatedCategory = await getAction('HrSetFileCategoryStatus')({
          parameters: { categoryId: String(category.categoryId) },
          payload: {
            version: category.version,
            status: nextStatus,
          },
        });
        const status = updatedCategory.status ?? nextStatus;
        node.setData({
          ...category,
          ...updatedCategory,
          status,
          rawStatus: status,
          _lifecycleActive: status === 'active' ? '1' : '0',
        });
        setRefreshToken((value) => value + 1);
      } catch (caught) {
        panic(caught as Error);
      }
    },
    [getAction]
  );

  const columns = useMemo<ColDef[]>(() => {
    const actionsWidth = getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode);

    return [
      {
        field: 'categoryId',
        headerName: 'ID',
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: HrIdCell,
      },
      {
        field: 'name',
        headerName: 'Názov',
        minWidth: 240,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: HrConfigurationNameCell,
      },
      {
        field: 'description',
        headerName: 'Popis',
        minWidth: 300,
        autoHeight: true,
        autoHeaderHeight: true,
        cellRenderer: HrLongTextCell,
      },
      {
        field: 'organizations',
        headerName: 'Vymedzenie štruktúry',
        minWidth: 300,
        valueGetter: ({ data }) =>
          data?.isGlobal
            ? 'Všetky organizácie'
            : (data?.organizations ?? []).map(({ organizationName }: any) => organizationName).join(', '),
        getQuickFilterText: ({ data }) =>
          data?.isGlobal
            ? 'Všetky organizácie'
            : (data?.organizations ?? []).map(({ organizationName }: any) => organizationName).join(' '),
        autoHeaderHeight: true,
        cellRenderer: HrScopeCell,
      },
      {
        field: 'displayOrder',
        headerName: 'Poradie zobrazenia',
        minWidth: 190,
        sortable: true,
        unSortIcon: true,
        autoHeaderHeight: true,
        cellRenderer: HrTextCell,
      },
      {
        field: '_lifecycleActive',
        headerName: 'Stav',
        hide: true,
        filter: true,
      },
      {
        field: '_actions',
        headerName: 'Akcie',
        valueGetter: hrActionCellVersion,
        pinned: 'right',
        width: actionsWidth,
        minWidth: actionsWidth,
        maxWidth: actionsWidth,
        autoHeaderHeight: true,
        cellRenderer: HrConfigurationActions,
        cellRendererParams: {
          editTarget: (category: any) =>
            HR_EDIT_FILE_CATEGORY_PAGE_PATH.insert({ categoryId: String(category.categoryId) }),
          entityNameAccusative: 'kategóriu HR súborov',
          onToggle: toggleStatus,
        },
      },
    ];
  }, [tableActionMode, toggleStatus]);

  if (roleId !== ROLE_ADMIN_ID) {
    return <Navigate to={DASHBOARD_PAGE_PATH.original} replace />;
  }

  return (
    <DataTable
      tableId="hr-file-categories"
      title="Kategórie HR súborov"
      action={load}
      columns={columns}
      dataRefreshToken={`file-categories:${refreshToken}`}
      addButtonText="Pridať kategóriu súborov"
      addButtonIcon={<IconPlus stroke={1.5} />}
      addButtonTarget={HR_CREATE_FILE_CATEGORY_PAGE_PATH.original}
      toggleDiscardedLabel="Zobraziť vyradené"
      discardedFilterField="_lifecycleActive"
      dataExport={{
        modalTitle: 'Exportovať kategórie HR súborov',
        fileName: 'hr-kategorie-suborov.xlsx',
        columnKeys: ['categoryId', 'name', 'description', 'organizations', 'displayOrder'],
      }}
      getRowId={({ data }) => String((data as { categoryId: number }).categoryId)}
    />
  );
}
